import React, { useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${theme.spacing.md};
`;

const Dialog = styled.div`
  background-color: white;
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.lg};
  max-width: 400px;
  width: 100%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const Title = styled.h3`
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.large};
  color: ${theme.colors.deepNavy};
  margin: 0 0 ${theme.spacing.md} 0;
  font-weight: 600;
`;

const Message = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
  line-height: 1.6;
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const InfoText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.fontSizes.small};
  color: ${theme.colors.slateGray};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing.lg} 0;
  font-style: italic;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.medium};
  font-family: ${theme.typography.headingFont};
  font-size: ${theme.typography.fontSizes.medium};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: none;

  &:focus {
    outline: 2px solid ${theme.colors.brandYellow};
    outline-offset: 2px;
  }
`;

const CancelButton = styled(Button)`
  background-color: ${theme.colors.softGray};
  color: ${theme.colors.deepNavy};

  &:hover {
    background-color: #d0d0d0;
  }
`;

const ConfirmButton = styled(Button)`
  background-color: #dc3545;
  color: white;

  &:hover {
    background-color: #c82333;
  }
`;

const ConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message,
  infoText,
  confirmText = "Confirm",
  cancelText = "Cancel"
}) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title>{title}</Title>
        <Message>{message}</Message>
        {infoText && <InfoText>{infoText}</InfoText>}
        <ButtonContainer>
          <CancelButton onClick={onClose}>{cancelText}</CancelButton>
          <ConfirmButton onClick={handleConfirm}>{confirmText}</ConfirmButton>
        </ButtonContainer>
      </Dialog>
    </Overlay>
  );
};

export default ConfirmDialog;
